/**
 * Idle detection for the reader-study active timer.
 *
 * Listens on `document` for any of mousemove / mousedown / wheel / keydown
 * / click / touchstart and tracks the most recent input timestamp. A 1Hz
 * setInterval checks `now - lastInputAt`; if it exceeds the threshold and
 * the timer was previously active, fires `onIdle`. The next input fires
 * `onActive`.
 *
 * Dictation suppression: while `suppressed` is true (e.g. mic streaming
 * during dictation), the timer is treated as active regardless of input —
 * the rad IS doing work, just not via mouse/keyboard.
 *
 * Returns the current `isIdle` boolean so consumers can render an idle
 * indicator. Edge callbacks (`onIdle`, `onActive`) fire exactly once at
 * the transition.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_THRESHOLD_MS = 30_000;
const TICK_INTERVAL_MS = 1_000;

const INPUT_EVENTS = ["mousemove", "mousedown", "wheel", "keydown", "click", "touchstart"] as const;

export interface UseIdleTimerOptions {
  /** Threshold in ms after last input before the user is considered idle. */
  thresholdMs?: number;
  /** While true, treat the user as active regardless of input. */
  suppressed?: boolean;
  /** Pause idle detection entirely (e.g. before the rad clicks Start). */
  disabled?: boolean;
  /** Fires once when the user transitions active → idle. */
  onIdle?: () => void;
  /** Fires once when the user transitions idle → active. */
  onActive?: () => void;
}

export interface UseIdleTimerResult {
  isIdle: boolean;
  /** Internal: number of ms since last input. Useful for tests + debug UI. */
  msSinceLastInput: () => number;
}

export function useIdleTimer(options: UseIdleTimerOptions = {}): UseIdleTimerResult {
  const {
    thresholdMs = DEFAULT_THRESHOLD_MS,
    suppressed = false,
    disabled = false,
    onIdle,
    onActive,
  } = options;

  const lastInputAtRef = useRef<number>(Date.now());
  // Idle state lives in a ref so the tick callback's reads/writes are
  // synchronous even when multiple fake-timer ticks fire inside one
  // React act(). State is mirrored separately for rendering.
  const isIdleRef = useRef(false);
  const [, setRenderToken] = useState(0);
  const forceRender = useCallback(() => {
    setRenderToken((n) => (n + 1) % 1_000_000);
  }, []);

  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  const onActiveRef = useRef(onActive);
  onActiveRef.current = onActive;
  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;

  useEffect(() => {
    if (disabled) return;
    const handleInput = () => {
      lastInputAtRef.current = Date.now();
    };
    for (const ev of INPUT_EVENTS) {
      // passive: true so the listener can't preventDefault and slow scroll.
      document.addEventListener(ev, handleInput, { passive: true, capture: true });
    }
    return () => {
      for (const ev of INPUT_EVENTS) {
        document.removeEventListener(ev, handleInput, { capture: true });
      }
    };
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;
    // Re-arm on every disabled→false transition. Otherwise the ref still holds
    // the mount timestamp, and the first tick after re-enable computes
    // elapsed ≫ thresholdMs (e.g. while the StudyLockScreen blocked input for
    // a DICOM stream) and immediately fires a phantom onIdle.
    lastInputAtRef.current = Date.now();
    const tick = () => {
      const now = Date.now();
      const elapsed = now - lastInputAtRef.current;
      const shouldBeIdle = !suppressedRef.current && elapsed >= thresholdMs;
      if (shouldBeIdle && !isIdleRef.current) {
        isIdleRef.current = true;
        forceRender();
        onIdleRef.current?.();
      } else if (!shouldBeIdle && isIdleRef.current) {
        isIdleRef.current = false;
        forceRender();
        onActiveRef.current?.();
      }
    };
    const handle = window.setInterval(tick, TICK_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [disabled, thresholdMs]);

  return {
    isIdle: isIdleRef.current,
    msSinceLastInput: () => Date.now() - lastInputAtRef.current,
  };
}
